import { useState } from 'react';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { Icon } from '../components/Icon';

/**
 * Notify owners — an incident happened, email whoever is responsible for the service.
 *
 * Preview-then-send is the safety property, not a nicety: you see exactly who will be
 * woken before anyone is. A one-shot button would mean finding out afterwards.
 *
 * Called "Notify owners" rather than "page": the only channel is email. Calling it a
 * page would promise an escalation system that does not exist.
 */
const SERVICES = gql`
  query IncidentServices {
    services {
      name
    }
  }
`;

const AUDIENCE = gql`
  query IncidentAudience($service: String!) {
    incidentAudience(service: $service) {
      service
      source
      fallbackReason
      emailConfigured
      lastNotifiedAt
      lastNotifiedBy
      repos {
        path
        archived
      }
      recipients {
        email
        displayName
        role
        via
      }
      unreachable {
        role
        repoPath
        gitlabUsername
        displayName
      }
    }
  }
`;

const NOTIFY = gql`
  mutation NotifyIncident($service: String!, $summary: String!, $details: String) {
    notifyIncident(service: $service, summary: $summary, details: $details) {
      status
      recipientCount
      recipients
      fallback
      error
      notifiedAt
    }
  }
`;

interface Recipient {
  email: string;
  displayName?: string;
  role: string;
  via: string[];
}
interface Unreachable {
  role: string;
  repoPath: string;
  gitlabUsername?: string;
  displayName?: string;
}
interface Audience {
  service: string;
  source: string;
  fallbackReason?: string;
  emailConfigured: boolean;
  lastNotifiedAt?: string;
  lastNotifiedBy?: string;
  repos: { path: string; archived: boolean }[];
  recipients: Recipient[];
  unreachable: Unreachable[];
}
interface NotifyResult {
  status: string;
  recipientCount: number;
  recipients: string[];
  fallback: boolean;
  error?: string;
  notifiedAt?: string;
}

const REASON: Record<string, string> = {
  no_repos: 'no repositories are linked to this service',
  no_owners: 'none of its repositories have an owner, maintainer or engineer',
  owners_have_no_email: 'the people assigned to it have no email address on file',
};

export function IncidentPage() {
  const [service, setService] = useState('');
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<NotifyResult | null>(null);

  const dir = useQuery<{ services: { name: string }[] }>(SERVICES, { fetchPolicy: 'cache-first' });
  const [loadAudience, aud] = useLazyQuery<{ incidentAudience: Audience }>(AUDIENCE, {
    fetchPolicy: 'network-only',
  });
  const [notify, { loading: sending }] = useMutation<{ notifyIncident: NotifyResult }>(NOTIFY, {
    onCompleted: (d) => {
      setResult(d.notifyIncident);
      setConfirming(false);
      if (d.notifyIncident.status === 'sent') setSummary('');
    },
    onError: (e) => {
      setResult({ status: 'failed', recipientCount: 0, recipients: [], fallback: false, error: e.message });
      setConfirming(false);
    },
  });

  const a = aud.data?.incidentAudience;
  const fallback = a?.source === 'layer1_fallback';
  const ready = !!a && a.emailConfigured && !!summary.trim() && !sending;

  const pick = (name: string) => {
    setService(name);
    setResult(null);
    setConfirming(false);
    if (name.trim()) void loadAudience({ variables: { service: name.trim() } });
  };

  return (
    <div className="page">
      <div className="detail-head">
        <h2>Notify owners</h2>
      </div>
      <p className="detail-desc">
        Something is broken — email the people responsible for a service. The audience comes from
        the owner, maintainers and engineers of the service&apos;s repositories. You will see
        exactly who is about to be emailed before anything is sent.
      </p>

      <section>
        <h3 className="section">1 · Which service</h3>
        <input
          type="text"
          list="incident-services"
          className="mcp-token-label-input"
          style={{ minWidth: 320 }}
          placeholder="start typing a service name…"
          value={service}
          onChange={(e) => pick(e.target.value)}
        />
        <datalist id="incident-services">
          {(dir.data?.services ?? []).map((s) => (
            <option key={s.name} value={s.name} />
          ))}
        </datalist>
        {aud.loading && <p className="muted text-sm">Resolving who owns it…</p>}
        {aud.error && <p className="error text-sm">{aud.error.message}</p>}
      </section>

      {a && (
        <>
          <section>
            <h3 className="section">2 · Who gets emailed</h3>

            {/* The fallback is the COMMON case, so it is a banner, not a footnote.
                Being woken because you own a thing and being woken because nobody
                does call for completely different responses. */}
            {fallback ? (
              <div className="dep-card" style={{ borderColor: 'var(--warn, #b8860b)' }}>
                <p className="text-sm" style={{ margin: 0 }}>
                  <Icon name="alert" /> <strong>Nobody owns {a.service}.</strong>
                </p>
                <p className="muted text-xs" style={{ marginBottom: 0 }}>
                  {REASON[a.fallbackReason ?? ''] ?? 'no owners could be resolved'} — so this would
                  email the <strong>Layer-1 access reviewers</strong> instead, who may know nothing
                  about this service. Assigning an owner on the repo is the real fix.
                </p>
              </div>
            ) : (
              <p className="muted text-sm">
                Resolved from {a.repos.length} repositor{a.repos.length === 1 ? 'y' : 'ies'} in this
                service.
              </p>
            )}

            {a.recipients.length ? (
              <table className="data-list--primary">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Via</th>
                  </tr>
                </thead>
                <tbody>
                  {a.recipients.map((r) => (
                    <tr key={r.email}>
                      <td>{r.displayName ? `${r.displayName} <${r.email}>` : r.email}</td>
                      <td>
                        <span className="tag-badge">{r.role.replace('_', ' ')}</span>
                      </td>
                      <td className="muted text-2xs">{r.via.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="error text-sm">
                No reachable recipients at all — not even a Layer-1 reviewer is configured. Nothing
                can be sent.
              </p>
            )}

            {/* Assigned but unreachable: the actionable gap, never hidden. */}
            {a.unreachable.length > 0 && (
              <p className="muted text-xs" style={{ marginTop: 8 }}>
                <Icon name="alert" /> Assigned but <strong>not reachable</strong> (no email on
                file):{' '}
                {a.unreachable
                  .map((u) => `${u.displayName ?? u.gitlabUsername ?? 'unknown'} (${u.role} of ${u.repoPath})`)
                  .join(' · ')}
              </p>
            )}

            {!a.emailConfigured && (
              <div className="dep-card" style={{ marginTop: 8 }}>
                <p className="text-sm" style={{ margin: 0 }}>
                  <Icon name="alert" /> Email is <strong>not configured</strong> in this environment
                  (<code>ACCESS_NOTIFY_FROM</code> is unset), so nothing can be sent from here.
                </p>
              </div>
            )}

            {a.lastNotifiedAt && (
              <p className="muted text-xs" style={{ marginTop: 8 }}>
                Already notified by {a.lastNotifiedBy} at{' '}
                {new Date(a.lastNotifiedAt).toLocaleTimeString()} — sending again is blocked for a
                few minutes.
              </p>
            )}
          </section>

          <section>
            <h3 className="section">3 · What is happening</h3>
            <input
              type="text"
              className="mcp-token-label-input"
              style={{ minWidth: 480 }}
              maxLength={200}
              placeholder="one line — e.g. 5xx on every request since 03:10"
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
                setConfirming(false);
              }}
            />
            <textarea
              className="mcp-token-label-input"
              style={{ minWidth: 480, minHeight: 80, display: 'block', marginTop: 8 }}
              placeholder="optional detail — what you have checked, dashboards, anything they need"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />

            <div className="dep-card" style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
              {confirming ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={sending}
                    onClick={() =>
                      void notify({
                        variables: { service: a.service, summary: summary.trim(), details: details.trim() || null },
                      })
                    }
                  >
                    {sending ? 'Sending…' : `Yes — email ${a.recipients.length} ${fallback ? 'reviewer(s)' : 'person/people'}`}
                  </button>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!ready || !a.recipients.length}
                  onClick={() => setConfirming(true)}
                >
                  Notify owners
                </button>
              )}
              <span className="muted text-xs">
                {confirming
                  ? 'This sends real email immediately.'
                  : 'You will be asked to confirm. You are always copied.'}
              </span>
            </div>
          </section>
        </>
      )}

      {result && (
        <section>
          <h3 className="section">Result</h3>
          {result.status === 'sent' && (
            <p className="text-sm">
              <Icon name="check" /> Emailed <strong>{result.recipientCount}</strong>{' '}
              {result.fallback ? 'Layer-1 reviewer(s) — nobody owns this service' : 'people'}:{' '}
              {result.recipients.join(', ')}
            </p>
          )}
          {result.status === 'cooldown' && <p className="text-sm">{result.error}</p>}
          {result.status === 'not_configured' && (
            <p className="error text-sm">
              Nothing sent — email is not configured in this environment.
            </p>
          )}
          {result.status === 'failed' && (
            // SES's own words: the likely first failure is an identity/sandbox
            // rejection naming the address, and paraphrasing would hide the fix.
            <p className="error text-sm">Nothing sent. {result.error}</p>
          )}
        </section>
      )}
    </div>
  );
}
